import * as verificationModule from './CallsLiveDeviceEvidenceVerificationEngine';

export type CallsLiveDeviceCertificationStatus =
  | 'certified'
  | 'not-certified';

export type CallsLiveDeviceCertificationReasonCode =
  | 'CALLS_LIVE_DEVICE_EVIDENCE_VERIFIED'
  | 'CALLS_LIVE_DEVICE_EVIDENCE_INCOMPLETE'
  | 'CALLS_LIVE_DEVICE_EVIDENCE_STALE'
  | 'CALLS_LIVE_DEVICE_VERIFICATION_UNAVAILABLE'
  | 'CALLS_LIVE_DEVICE_VERIFICATION_FAILED';

export interface CallsLiveDeviceCertificationResult {
  readonly governor: 'CallsGovernor';
  readonly domain: 'calls';
  readonly lifecycle: 'runtime-certification';
  readonly evidenceSource: 'calls-live-device-verification';
  readonly status: CallsLiveDeviceCertificationStatus;
  readonly certified: boolean;
  readonly reasonCode: CallsLiveDeviceCertificationReasonCode;
  readonly reason: string;
  readonly verificationStatus: string;
  readonly missingEvidence: readonly string[];
  readonly evidenceCapturedAt: string | null;
  readonly certificationIssuedAt: string;
}

type UnknownRecord = Readonly<Record<string, unknown>>;

const MAX_EVIDENCE_AGE_MS = 15 * 60 * 1000;

function asRecord(value: unknown): UnknownRecord {
  return value !== null && typeof value === 'object'
    ? (value as UnknownRecord)
    : Object.freeze({});
}

function getString(
  record: UnknownRecord,
  keys: readonly string[],
): string | null {
  for (const key of keys) {
    const value = record[key];

    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
  }

  return null;
}

function getBoolean(
  record: UnknownRecord,
  keys: readonly string[],
): boolean | null {
  for (const key of keys) {
    const value = record[key];

    if (typeof value === 'boolean') {
      return value;
    }
  }

  return null;
}

function getStringArray(
  record: UnknownRecord,
  keys: readonly string[],
): readonly string[] {
  for (const key of keys) {
    const value = record[key];

    if (Array.isArray(value)) {
      return Object.freeze(
        value.filter(
          (entry): entry is string =>
            typeof entry === 'string' && entry.trim().length > 0,
        ),
      );
    }
  }

  return Object.freeze([]);
}

function resolveVerificationEntrypoint(): (() => unknown) | null {
  const moduleRecord =
    verificationModule as unknown as Record<string, unknown>;

  const candidates = [
    'verifyCallsLiveDeviceEvidence',
    'runCallsLiveDeviceEvidenceVerification',
    'evaluateCallsLiveDeviceEvidenceVerification',
    'getCallsLiveDeviceEvidenceVerification',
  ] as const;

  for (const candidate of candidates) {
    const entrypoint = moduleRecord[candidate];

    if (typeof entrypoint === 'function') {
      return entrypoint as () => unknown;
    }
  }

  return null;
}

function isEvidenceStale(capturedAt: string | null): boolean {
  if (!capturedAt) {
    return false;
  }

  const capturedTime = Date.parse(capturedAt);

  if (!Number.isFinite(capturedTime)) {
    return true;
  }

  return Date.now() - capturedTime > MAX_EVIDENCE_AGE_MS;
}

function createResult(
  input: Omit<
    CallsLiveDeviceCertificationResult,
    | 'governor'
    | 'domain'
    | 'lifecycle'
    | 'evidenceSource'
    | 'certificationIssuedAt'
  >,
): CallsLiveDeviceCertificationResult {
  return Object.freeze({
    governor: 'CallsGovernor',
    domain: 'calls',
    lifecycle: 'runtime-certification',
    evidenceSource: 'calls-live-device-verification',
    certificationIssuedAt: new Date().toISOString(),
    ...input,
    missingEvidence: Object.freeze([...input.missingEvidence]),
  });
}

export function certifyCallsLiveDeviceEvidence():
  CallsLiveDeviceCertificationResult {
  const verify = resolveVerificationEntrypoint();

  if (!verify) {
    return createResult({
      status: 'not-certified',
      certified: false,
      reasonCode: 'CALLS_LIVE_DEVICE_VERIFICATION_UNAVAILABLE',
      reason:
        'Calls live-device certification was denied because the CG-022 verification entrypoint could not be resolved.',
      verificationStatus: 'unavailable',
      missingEvidence: Object.freeze([
        'calls-live-device-verification-entrypoint',
      ]),
      evidenceCapturedAt: null,
    });
  }

  try {
    const verification = asRecord(verify());

    const verificationStatus =
      getString(verification, [
        'status',
        'verificationStatus',
        'verdict',
      ]) ?? 'unknown';

    const explicitlyVerified = getBoolean(verification, [
      'verified',
      'isVerified',
      'success',
      'passed',
      'authorized',
    ]);

    const missingEvidence = getStringArray(verification, [
      'missingEvidence',
      'missing',
      'unresolvedEvidence',
      'failedChecks',
    ]);

    const evidenceCapturedAt = getString(verification, [
      'evidenceCapturedAt',
      'capturedAt',
      'verifiedAt',
      'timestamp',
    ]);

    const normalizedStatus = verificationStatus
      .trim()
      .toLowerCase();

    const statusIndicatesVerified = [
      'verified',
      'pass',
      'passed',
      'authorized',
      'healthy',
      'complete',
    ].includes(normalizedStatus);

    const verified =
      explicitlyVerified === true ||
      (explicitlyVerified !== false &&
        statusIndicatesVerified &&
        missingEvidence.length === 0);

    if (missingEvidence.length > 0) {
      return createResult({
        status: 'not-certified',
        certified: false,
        reasonCode: 'CALLS_LIVE_DEVICE_EVIDENCE_INCOMPLETE',
        reason:
          'Calls live-device certification was denied because required live-device evidence is incomplete.',
        verificationStatus,
        missingEvidence,
        evidenceCapturedAt,
      });
    }

    if (isEvidenceStale(evidenceCapturedAt)) {
      return createResult({
        status: 'not-certified',
        certified: false,
        reasonCode: 'CALLS_LIVE_DEVICE_EVIDENCE_STALE',
        reason:
          'Calls live-device certification was denied because the verified evidence is stale.',
        verificationStatus,
        missingEvidence: Object.freeze([]),
        evidenceCapturedAt,
      });
    }

    if (!verified) {
      return createResult({
        status: 'not-certified',
        certified: false,
        reasonCode: 'CALLS_LIVE_DEVICE_VERIFICATION_FAILED',
        reason:
          'Calls live-device certification was denied because CG-022 did not produce a verified result.',
        verificationStatus,
        missingEvidence: Object.freeze([]),
        evidenceCapturedAt,
      });
    }

    return createResult({
      status: 'certified',
      certified: true,
      reasonCode: 'CALLS_LIVE_DEVICE_EVIDENCE_VERIFIED',
      reason:
        'Calls live-device evidence is complete, current, and verified for governed runtime use.',
      verificationStatus,
      missingEvidence: Object.freeze([]),
      evidenceCapturedAt,
    });
  } catch {
    return createResult({
      status: 'not-certified',
      certified: false,
      reasonCode: 'CALLS_LIVE_DEVICE_VERIFICATION_FAILED',
      reason:
        'Calls live-device certification was denied because CG-022 verification threw an exception.',
      verificationStatus: 'error',
      missingEvidence: Object.freeze([]),
      evidenceCapturedAt: null,
    });
  }
}
